import { useEffect, useMemo, useState } from "react";
import { TextField, Box, Typography, CircularProgress } from "@mui/material";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import Drawer from "../../../components/ui/Drawer";
import { SoftButton } from "../../hatim/soft/SoftKit";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { parseJoinCode } from "../../../lib/joinCodeParser";
import {
  useLazyGetGroupPublicInfoQuery,
  useJoinGroupByQRMutation,
} from "../../../api/groupsApi";

export default function JoinGroupModal({ open, onClose, onJoined }) {
  const { t } = useScriptMode();
  const [raw, setRaw] = useState("");
  const [submitError, setSubmitError] = useState("");

  const parsed = useMemo(() => parseJoinCode(raw), [raw]);
  const [trigger, { data: lookup, isFetching, isError: lookupError }] =
    useLazyGetGroupPublicInfoQuery();
  const [joinGroup, { isLoading: joinLoading }] = useJoinGroupByQRMutation();

  useEffect(() => {
    if (!parsed) return;
    const handle = setTimeout(() => {
      trigger({ groupId: parsed.groupId, templateId: parsed.templateId });
    }, 200);
    return () => clearTimeout(handle);
  }, [parsed, trigger]);

  useEffect(() => {
    if (!open) {
      setRaw("");
      setSubmitError("");
    }
  }, [open]);

  const info = lookup?.data;
  const canJoin = !!parsed && !!info && !lookupError && !joinLoading;
  const helperText =
    raw && !parsed
      ? t("group_join_parse_error")
      : raw && parsed && lookupError
      ? t("group_join_lookup_failed")
      : t("group_join_help");

  const handleSubmit = async () => {
    if (!parsed) return;
    try {
      const result = await joinGroup({
        groupId: parsed.groupId,
        templateId: parsed.templateId,
      }).unwrap();
      const groupName = result?.data?.groupName || info?.groupName || "";
      onJoined && onJoined(groupName);
      onClose && onClose();
    } catch (err) {
      const status = err?.status;
      const msg = err?.message || err?.data?.message || "";
      if (status === 400 && /üye/i.test(msg)) {
        setSubmitError(t("group_join_already_member"));
      } else {
        setSubmitError(msg || t("group_join_error"));
      }
    }
  };

  // Hatime Katıl ile aynı dar çekmece.
  return (
    <Drawer
      open={!!open}
      onClose={onClose}
      width="sm"
      title={t("group_join_modal_title")}
      titleIcon={<GroupAddOutlinedIcon />}
      footer={
        <div className="flex gap-2">
          <SoftButton tone="soft" fullWidth onClick={onClose}>
            {t("group_join_cancel")}
          </SoftButton>
          <SoftButton tone="primary" fullWidth onClick={handleSubmit} disabled={!canJoin}>
            {t("group_join_confirm_cta")}
          </SoftButton>
        </div>
      }
    >
      <div className="px-5 py-3">
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={t("group_join_input_label")}
          placeholder={t("group_join_placeholder")}
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            setSubmitError("");
          }}
          error={(!!raw && !parsed) || !!lookupError}
          helperText={helperText}
          margin="normal"
        />
        {parsed && isFetching ? (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">…</Typography>
          </Box>
        ) : null}
        {parsed && info ? (
          <Box
            mt={2}
            p={2}
            sx={{
              borderRadius: 2,
              backgroundColor: "rgba(46,125,98,0.08)",
              border: "1px solid rgba(46,125,98,0.2)",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t("group_join_detected")}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {info.groupName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {info.templateName}
            </Typography>
          </Box>
        ) : null}
        {submitError ? (
          <Typography color="error" variant="body2" mt={2}>
            {submitError}
          </Typography>
        ) : null}
      </div>
    </Drawer>
  );
}
